import { AlertCircle, Check, Mail, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { Flex, Heading } from '@radix-ui/themes';
import { Box, Button, Text } from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { useAuth } from '@/hooks/useAuth';
import { analyticsClient } from '@/lib';
import { mapVerificationSendError } from '@/lib/auth';
import { authService } from '@/services';

/** Post-signup — wait for email verification link. */
export function AuthVerification() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);

  const email = user?.email ?? '';

  const handleConfirm = async () => {
    setIsVerifying(true);
    setError(null);
    try {
      const isVerified = await authService.checkEmailVerificationStatus();
      if (isVerified) {
        analyticsClient.capture('email_verified', { method: 'email_link' });
        navigate('/');
      } else {
        setError(t('errors.emailNotVerified'));
      }
    } catch {
      setError(t('errors.generic'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError(null);
    try {
      await authService.resendVerificationEmail();
      setResent(true);
    } catch (err) {
      setError(t(mapVerificationSendError(err)));
    } finally {
      setIsResending(false);
    }
  };

  const info = resent ? t('verification.resent') : t('verification.emailSent');

  return (
    <WizardScreen header={<WizardHeader withLogoutButton />}>
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="4"
        flexGrow="1"
        minHeight="0"
        height="100%"
      >
        <Box
          p="3"
          style={{
            borderRadius: '50%',
            border: '1px solid var(--accent-a6)',
            background: 'var(--accent-a3)',
          }}
        >
          <Mail size={24} className="text-accent" />
        </Box>

        <Heading size="4">{t('verification.title')}</Heading>
        <Text size="2" color="gray" align="center">
          {t('verification.description', { email })}
        </Text>

        {error && (
          <Flex align="center" gap="2" p="3" width="100%">
            <AlertCircle size={16} className="shrink-0 text-destructive" />
            <Text size="1" className="text-destructive">
              {error}
            </Text>
          </Flex>
        )}

        {info && (
          <Flex align="center" gap="2" p="3" width="100%">
            <Sparkles size={16} className="shrink-0 text-accent" />
            <Text size="1" color="gray">
              {info}
            </Text>
          </Flex>
        )}

        <Button
          type="button"
          onClick={handleConfirm}
          disabled={isVerifying}
          color="gray"
          highContrast
          fullWidth
        >
          <Check size={16} />
          {isVerifying ? t('verification.verifyingButton') : t('verification.confirmButton')}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={handleResend}
          disabled={isResending}
          fullWidth
        >
          <Mail size={16} />
          {isResending ? t('verification.resendingButton') : t('verification.resendButton')}
        </Button>
      </Flex>
    </WizardScreen>
  );
}
